<template>
  <div>
    <div v-if="!forAppEmbed" class="prospect-drawer-backdrop" @click="close"></div>
    <aside ref="drawer" tabindex="-1" class="prospect-drawer" :class="{ 'is-embed-page': forAppEmbed }" role="dialog" aria-label="Coach private lesson setup" @keydown.esc="close">
      <header class="drawer-header">
        <div class="flex align-items-center gap-sm flex-1" style="min-width: 0;">
          <img v-if="coach.avatar" :src="coach.avatar" class="pl-drawer-avatar" alt="Coach avatar" />
          <div v-else class="pl-drawer-avatar bg-gray-200 flex align-items-center justify-center flex-shrink-0">
            <span class="text-gray-500 font-semi">{{ coachInitials }}</span>
          </div>
          <div style="min-width: 0;">
            <h3 class="m-y-none font-heavy text-20">{{ coach.full_name }}</h3>
            <p class="m-y-none drawer-subtitle">Private lesson setup</p>
          </div>
        </div>
        <!-- As a pushed native screen the navbar back button is the close -->
        <button v-if="!forAppEmbed" type="button" class="btn btn-default" aria-label="Close" @click="close">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </header>

      <div class="prospect-drawer-body p-md">
        <div class="form-group">
          <label class="m-b-none">Photo</label>
          <p class="text-gray-600 m-b-xs">
            Shown to families when booking.
          </p>
          <pintura-photo-uploader
            :access-key="accessKey"
            :endpoint="endpoint"
            :cloudfront-base-url="cloudfrontBaseUrl"
            :existing-attachments="existingAttachments"
            :attachable-id="coach.id"
            attachable-type="CoachProfile"
            prefix="profile_photos/"
            imageCropAspectRatio="1"
            @photos-changed="photosChanged"
            @busy-changed="uploaderBusy = $event"
          ></pintura-photo-uploader>
        </div>

        <div class="form-group flex align-items-start">
          <input id="drawer-accepting-private-lessons" type="checkbox" v-model="accepting">
          <div class="m-l-sm">
            <label for="drawer-accepting-private-lessons" class="m-b-none">
              Accepting private lessons?
            </label>
            <p class="text-gray-600 m-b-none">
              When unchecked, this coach is hidden from the booking flow.
            </p>
          </div>
        </div>
        <div class="alert alert-danger" v-if="hadPreviouslyAcceptedPrivateLessons && !accepting">
          Turning off private lesson availability does not impact existing bookings. Please cancel any existing bookings if you need to cancel existing lessons after turning off availability.
        </div>

        <div class="form-group">
          <label class="m-b-none">Bio (optional, but recommended)</label>
          <p class="text-gray-600 m-b-xs">
            Describe this coach's style, philosophy, and what to expect from working with them.
          </p>
          <div class="pl-bio-editor">
            <input id="drawer-coach-bio" type="hidden" name="content">
            <trix-editor input="drawer-coach-bio" class="trix-content"></trix-editor>
          </div>
        </div>

        <!-- Section: Pricing. Only admins can change what a coach charges, so a
             coach managing their own setup sees bio/photo/availability only. -->
        <template v-if="canEditPricing">
        <h4 class="drawer-section-heading">Pricing</h4>
        <div class="flex align-items-center justify-space-between m-b-xs">
          <p class="text-gray-600 m-y-none">
            Pick the pricing tiers {{ coach.first_name }} offers. Tiers are shared — multiple coaches can use the same one.
          </p>
          <button type="button" class="btn btn-default btn-compact flex-shrink-0 m-l-xs" @click="openNewTierForm()">
            <i class="fa-solid fa-plus m-r-xxs"></i> New pricing tier
          </button>
        </div>

        <div v-if="!productsLoaded" class="text-gray-500 text-12 p-y-sm">
          Loading pricing tiers...
        </div>
        <div v-else-if="productsError" class="alert alert-danger">
          Could not load pricing tiers: {{ productsError }}
        </div>
        <div v-else-if="products.length === 0 && !showTierForm" class="pl-tier-empty">
          <p class="font-semi m-b-xxs">No pricing tiers yet</p>
          <p class="text-gray-600 m-b-sm">A tier is a shared price list, like "Head Coach Privates", with a price point for each group size you offer.</p>
          <button type="button" class="btn btn-primary" @click="openNewTierForm()">
            <i class="fa-solid fa-plus m-r-xxs"></i> Create your first pricing tier
          </button>
        </div>

        <div v-for="product in products" :key="product.id" class="pl-tier" :class="{ 'is-selected': isSelected(product) }">
          <div class="flex align-items-start gap-sm p-sm">
            <input type="checkbox" :id="`pl-tier-${product.id}`" :checked="isSelected(product)" @change="toggleListing(product)" class="m-t-xxs">
            <div class="flex-1" style="min-width: 0;">
              <div class="flex align-items-center gap-x-xs flex-wrap">
                <label :for="`pl-tier-${product.id}`" class="m-b-none font-semi cursor-pointer">{{ product.name }}</label>
                <span class="pl-tier-badge">{{ sharedText(product) }}</span>
              </div>
              <p class="text-12 text-gray-500 m-y-none">{{ planSummary(product) }}</p>
            </div>
            <button type="button" class="btn btn-link btn-compact text-12 flex-shrink-0 p-x-n" @click="toggleTierEditor(product)">
              {{ editingProductId === product.id ? 'Close' : 'Edit prices' }}
            </button>
          </div>

          <!-- Inline tier editor -->
          <div v-if="editingProductId === product.id && tierDraft" class="pl-tier-editor">
            <div v-if="otherCoachCount(product) > 0" class="pl-tier-warning">
              <i class="fa-solid fa-triangle-exclamation m-r-xxs"></i>
              This tier is shared with {{ otherCoachCount(product) }} other {{ otherCoachCount(product) === 1 ? 'coach' : 'coaches' }} — price changes apply to everyone using it.
              <a href="javascript:void(0)" class="text-underline font-semi" @click="openNewTierForm(product)">Duplicate as a new tier instead</a>
            </div>
            <tier-fields :draft="tierDraft" :currency-symbol="currencySymbol"></tier-fields>
            <div class="flex justify-end gap-x-xs m-t-sm">
              <button type="button" class="btn btn-default btn-compact" @click="closeTierEditor">Cancel</button>
              <ladda-button @lbClicked="saveTier" el-class="btn-primary btn-compact" :loading="savingTier">
                Save prices
              </ladda-button>
            </div>
            <p v-if="tierError" class="text-danger text-12 m-t-xs m-b-none">{{ tierError }}</p>
          </div>
        </div>

        <!-- Inline new tier form -->
        <div v-if="showTierForm" class="pl-tier-editor is-new m-t-sm">
          <p class="font-semi m-y-none">New pricing tier</p>
          <p class="text-12 text-gray-500 m-t-xxs m-b-sm">
            e.g. "Head Coach Privates" — add a price point for each group size you offer. Prices are per hour, per wrestler.
          </p>
          <div class="form-group">
            <label class="text-12 m-b-xxs">Tier name</label>
            <input type="text" v-model="tierDraft.name" class="form-control" placeholder="e.g. Head Coach Privates">
          </div>
          <tier-fields :draft="tierDraft" :currency-symbol="currencySymbol"></tier-fields>
          <div class="flex justify-end gap-x-xs m-t-sm">
            <button type="button" class="btn btn-default btn-compact" @click="closeTierEditor">Cancel</button>
            <ladda-button @lbClicked="saveTier" el-class="btn-primary btn-compact" :loading="savingTier">
              Create &amp; assign to {{ coach.first_name }}
            </ladda-button>
          </div>
          <p v-if="tierError" class="text-danger text-12 m-t-xs m-b-none">{{ tierError }}</p>
        </div>
        </template>
      </div>

      <!-- In the embed the native navbar provides Save (bridge) and back is
           cancel, so the web footer would just duplicate both -->
      <footer v-if="!forAppEmbed" class="pl-drawer-footer">
        <div v-if="error" class="text-danger text-12 m-r-sm">{{ errorMessage }}</div>
        <button type="button" class="btn btn-default" @click="close">Cancel</button>
        <ladda-button @lbClicked="saveCoach" el-class="btn-primary" :loading="loading">
          Save
        </ladda-button>
      </footer>
    </aside>
  </div>
</template>

<script>
import { errorableMix } from './mixins/errorable_mix';
import { currencyMix } from '../../shared/currency_mix';
import { photoUploadMix } from './mixins/photo_upload_mix';
import { appEmbedMix } from './mixins/app_embed_mix';
import LaddaButton from '../../shared/ladda_button.vue';
import PinturaPhotoUploader from './pintura_photo_uploader.vue';

// Small internal component for the shared price point grid, used by both the
// inline tier editor and the new tier form.
const TierFields = {
  name: 'tier-fields',
  props: {
    draft: { type: Object, required: true },
    currencySymbol: { type: String, default: '$' },
  },
  template: `
    <div>
      <div v-for="(plan, idx) in visiblePlans" :key="idx" class="pl-plan-card">
        <div class="flex justify-space-between align-items-baseline m-b-xs">
          <p class="text-10 text-gray-500 font-semi uppercase m-y-none">Price point {{ idx + 1 }}</p>
          <button type="button" class="btn btn-link btn-compact text-gray-500 p-x-n" title="Remove price point" @click="removePlan(plan)">
            <i class="fa-solid fa-xmark"></i><span class="m-l-xxs">Remove</span>
          </button>
        </div>
        <label class="pl-plan-field m-b-xs" style="display: block;">
          <span class="pl-plan-field-label">Name</span>
          <input type="text" v-model="plan.name" class="form-control input-sm" placeholder="e.g. 1-on-1">
        </label>
        <div class="grid grid-cols-2 sm-grid-cols-1 gap-x-sm gap-y-xs m-b-xs">
          <label class="pl-plan-field">
            <span class="pl-plan-field-label">Max wrestlers in lesson</span>
            <input type="number" min="1" v-model.number="plan.max_wrestler_count" class="form-control input-sm" placeholder="1">
          </label>
          <label class="pl-plan-field">
            <span class="pl-plan-field-label">Price per hour, per wrestler</span>
            <span class="pl-plan-input-wrap">
              <span class="pl-input-prefix">{{ currencySymbol }}</span>
              <input type="number" min="0" step="0.01" v-model="plan.price_dollars" class="form-control input-sm pl-input-with-prefix" placeholder="60">
            </span>
          </label>
        </div>
        <!-- Fixed control widths because .form-control's width: 100% resolves
             unpredictably as a flex item -->
        <div class="pl-plan-facility">
          <span class="text-12 text-gray-600 font-semi flex-shrink-0">Facility fee</span>
          <select v-model="plan.facility_fee_type" class="form-control input-sm" style="width: 110px; flex: none;">
            <option value="percent">% of price</option>
            <option value="hourly">{{ currencySymbol }}/hr</option>
          </select>
          <span v-if="plan.facility_fee_type !== 'percent'" class="pl-plan-input-wrap" style="width: 130px; flex: none;">
            <span class="pl-input-prefix">{{ currencySymbol }}</span>
            <input type="number" min="0" step="0.01" v-model="plan.facility_dollars" class="form-control input-sm pl-input-with-prefix" placeholder="0">
          </span>
          <span v-else class="pl-plan-input-wrap" style="width: 130px; flex: none;">
            <input type="number" min="1" max="99" step="1" v-model.number="plan.facility_percent" class="form-control input-sm pl-input-with-suffix" placeholder="0">
            <span class="pl-input-suffix">%</span>
          </span>
          <span class="pl-plan-facility-hint">The facility's cut of this price point, deducted when calculating coach payroll. Leave blank for no fee.</span>
        </div>
      </div>
      <button type="button" class="pl-add-plan" @click="addPlan">
        <i class="fa-solid fa-plus"></i>
        <span>
          Add another price point
          <span class="pl-add-plan-hint">e.g. a 2-on-1 or small group rate</span>
        </span>
      </button>
    </div>
  `,
  computed: {
    visiblePlans() {
      return this.draft.plans.filter((plan) => !plan._destroy);
    },
  },
  methods: {
    addPlan() {
      this.draft.plans.push({
        id: null,
        name: '',
        max_wrestler_count: null,
        price_dollars: '',
        facility_fee_type: 'percent',
        facility_dollars: '',
        facility_percent: null,
        _destroy: false,
      });
    },
    removePlan(plan) {
      if (plan.id) {
        this.$set(plan, '_destroy', true);
      } else {
        this.draft.plans.splice(this.draft.plans.indexOf(plan), 1);
      }
    },
  },
};

export default {
  name: 'private-lesson-coach-drawer',
  components: {
    LaddaButton,
    PinturaPhotoUploader,
    TierFields,
  },
  mixins: [errorableMix, currencyMix, photoUploadMix, appEmbedMix],
  props: {
    coach: {
      type: Object,
      required: true,
    },
    currency: {
      type: String,
      default: 'usd',
    },
    // Every call site passes this explicitly; default matches the panel's
    // (deny unless told otherwise)
    canEditPricing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // True from photo pick (Pintura editor open) through processed/removed
      uploaderBusy: false,
      accepting: this.coach.accepting_private_lessons,
      hadPreviouslyAcceptedPrivateLessons: this.coach.accepting_private_lessons,
      bioTrixEditor: null,
      products: [],
      productsLoaded: false,
      productsError: '',
      // product_id -> count of coaches (including this one) offering the tier
      tierUsage: {},
      // Working copy of the coach's product listings, product_coach_listing rows
      // with the {id, product_id, _destroy} shape the API expects.
      listings: [],
      editingProductId: null,
      showTierForm: false,
      tierDraft: null,
      tierError: '',
      savingTier: false,
      loading: false,
    };
  },
  computed: {
    // Busy from the moment a photo is picked (uploaderBusy spans the Pintura
    // editor being open and processing) through S3 confirm (photoUploadInFlight)
    photoWorkInProgress() {
      return this.uploaderBusy || this.photoUploadInFlight;
    },
    coachInitials() {
      return `${(this.coach.first_name || ' ')[0] || ''}${(this.coach.last_name || ' ')[0] || ''}`.trim();
    },
    currencySymbol() {
      // formatAsCurrency from currencyMix renders full amounts; for input
      // prefixes we just need the symbol.
      return this.formatAsCurrency(0).replace(/[\d.,\s]/g, '') || '$';
    },
    listingParams() {
      return this.listings.map((listing) => ({
        id: listing.id,
        product_id: listing.product_id,
        _destroy: listing._destroy,
      }));
    },
  },
  created() {
    this.listings = _.cloneDeep(this.coach.product_coach_listings || []);
    if (this.canEditPricing) {
      this.fetchProducts();
      this.fetchTierUsage();
    }
  },
  mounted() {
    // Move focus into the drawer so keyboard and screen reader users land here
    // instead of continuing to tab through the page behind the backdrop.
    if (this.$refs.drawer) {
      this.$refs.drawer.focus();
    }
    document.addEventListener('trix-initialize', this.trixInit);
    document.addEventListener('trix-file-accept', this.trixFileAccept);
    document.addEventListener('keydown', this.onEsc);
    // Save lives in the native navbar when pushed as its own screen
    if (this.forAppEmbed) {
      this.setupNativeSaveButton(this.saveCoach);
    }
  },
  beforeDestroy() {
    document.removeEventListener('trix-initialize', this.trixInit);
    document.removeEventListener('trix-file-accept', this.trixFileAccept);
    document.removeEventListener('keydown', this.onEsc);
  },
  watch: {
    // Fast pick + immediate save raced the S3 upload and saved without the
    // photo (or with the editor still open). Pull the native save button while
    // photo work runs; the saveCoach guard covers the web button and any
    // in-flight-message race.
    photoWorkInProgress(busy) {
      if (this.forAppEmbed) {
        this.setNativeSaveVisible(!busy);
      }
      if (!busy && this.errorMessage === 'Your photo is still uploading — one moment.') {
        this.error = false;
        this.errorMessage = '';
      }
    },
  },
  methods: {
    close() {
      // As a pushed native screen, cancel means pop back to the profile page
      if (this.forAppEmbed) {
        this.askAppToPop();
        return;
      }
      this.$emit('close');
    },
    onEsc(e) {
      if (e.key === 'Escape') this.close();
    },
    trixInit(event) {
      if (event.target.inputElement.id === 'drawer-coach-bio') {
        this.bioTrixEditor = event.target.editor;
        if (this.coach.bio) {
          this.bioTrixEditor.loadHTML(this.coach.bio);
        }
      }
    },
    // The bio toolbar hides the attach button, but files can still arrive via
    // drag-and-drop or paste — reject those too since nothing uploads them.
    trixFileAccept(event) {
      if (event.target.inputElement && event.target.inputElement.id === 'drawer-coach-bio') {
        event.preventDefault();
      }
    },
    fetchProducts() {
      const url = `${this.$apiService.productsUrl()}?q[category_eq]=private_lesson&q[s]=name asc&per_page=100`;
      axios.get(url)
        .then((response) => {
          this.products = response.data.products;
          this.productsError = '';
          this.productsLoaded = true;
        })
        .catch((error) => {
          // Without this the empty state claims there are no tiers, and a coach
          // gets a duplicate of a tier that already exists.
          this.productsError = this.parseErrorResponse(error);
          this.productsLoaded = true;
        });
    },
    // Pull every coach once so tiers can show "shared with N other coaches".
    // Purely informational, so a fetch failure just hides the counts.
    fetchTierUsage() {
      this.$apiService.loadAllPages(`${this.$apiService.coachesUrl()}?per_page=100`, 'coaches')
        .then((coaches) => {
          const usage = {};
          coaches.forEach((c) => {
            (c.product_coach_listings || []).forEach((listing) => {
              usage[listing.product_id] = (usage[listing.product_id] || 0) + (c.id === this.coach.id ? 0 : 1);
            });
          });
          this.tierUsage = usage;
        })
        .catch(() => {});
    },
    activePlans(product) {
      return (product.billing_plans || []).filter((plan) => plan.active);
    },
    planSummary(product) {
      const plans = this.activePlans(product);
      if (plans.length === 0) {
        return 'No active price points';
      }
      return plans.map((plan) => `${plan.name}: ${this.formatAsCurrency(plan.amount)}/hr per wrestler`).join(' · ');
    },
    otherCoachCount(product) {
      return this.tierUsage[product.id] || 0;
    },
    sharedText(product) {
      const count = this.otherCoachCount(product);
      if (count === 0) {
        return 'not shared';
      }
      return `shared with ${count} other ${count === 1 ? 'coach' : 'coaches'}`;
    },
    isSelected(product) {
      const listing = this.listings.find((l) => l.product_id === product.id);
      return listing ? !listing._destroy : false;
    },
    toggleListing(product) {
      const existingListing = this.listings.find((l) => l.product_id === product.id);
      if (existingListing) {
        if (!existingListing.id) {
          this.listings.splice(this.listings.indexOf(existingListing), 1);
        } else {
          this.$set(existingListing, '_destroy', !existingListing._destroy);
        }
      } else {
        this.listings.push({
          id: null,
          product_id: product.id,
        });
      }
    },
    // ---- Tier editing ----
    draftFromProduct(product, asDuplicate) {
      return {
        id: asDuplicate ? null : product.id,
        name: asDuplicate ? `${product.name} (copy)` : product.name,
        plans: this.activePlans(product).map((plan) => ({
          id: asDuplicate ? null : plan.id,
          name: plan.name,
          max_wrestler_count: plan.max_wrestler_count,
          price_dollars: (plan.amount / 100.0).toString(),
          facility_fee_type: plan.facility_fee_type || 'hourly',
          facility_dollars: ((plan.facility_price_per_hour || 0) / 100.0).toString(),
          // Stored as a 0-1 decimal, edited as a whole percent.
          facility_percent: plan.facility_percent_fee ? _.round(plan.facility_percent_fee * 100) : null,
          _destroy: false,
        })),
      };
    },
    blankDraft() {
      return {
        id: null,
        name: '',
        plans: [{
          id: null,
          name: '1-on-1',
          max_wrestler_count: 1,
          price_dollars: '',
          facility_fee_type: 'percent',
          facility_dollars: '',
          facility_percent: null,
          _destroy: false,
        }],
      };
    },
    toggleTierEditor(product) {
      if (this.editingProductId === product.id) {
        this.closeTierEditor();
        return;
      }
      this.showTierForm = false;
      this.editingProductId = product.id;
      this.tierDraft = this.draftFromProduct(product, false);
      this.tierError = '';
    },
    openNewTierForm(duplicateFrom) {
      this.editingProductId = null;
      this.tierDraft = duplicateFrom ? this.draftFromProduct(duplicateFrom, true) : this.blankDraft();
      this.tierError = '';
      this.showTierForm = true;
    },
    closeTierEditor() {
      this.editingProductId = null;
      this.showTierForm = false;
      this.tierDraft = null;
      this.tierError = '';
    },
    validateTierDraft() {
      if (!this.tierDraft.name || !this.tierDraft.name.trim()) {
        this.tierError = 'Tier name is required.';
        return false;
      }
      const plans = this.tierDraft.plans.filter((plan) => !plan._destroy);
      if (plans.length === 0) {
        this.tierError = 'At least one price point is required.';
        return false;
      }
      // Number inputs hand back strings, and min="0" isn't enforced without a
      // form submit, so negatives have to be rejected here or they persist as
      // negative amounts.
      const invalidPrice = (value, allowBlank) => {
        if (value === '' || value === null || value === undefined) {
          return !allowBlank;
        }
        const parsed = parseFloat(value);
        return Number.isNaN(parsed) || parsed < 0;
      };
      // A blank percent means "no facility fee" (normalized on save), so only
      // reject values that are present but outside 1-99. Whole numbers only —
      // serialization rounds, so 1.5 would silently persist as 2%.
      const invalidPercent = (plan) => {
        if (plan.facility_fee_type !== 'percent') {
          return false;
        }
        if (plan.facility_percent === null || plan.facility_percent === '' || plan.facility_percent === undefined) {
          return false;
        }
        const parsed = Number(plan.facility_percent);
        return !Number.isInteger(parsed) || parsed < 1 || parsed > 99;
      };
      const invalid = plans.some((plan) => !plan.name
        || !plan.max_wrestler_count
        || plan.max_wrestler_count < 1
        || invalidPrice(plan.price_dollars, false)
        || invalidPrice(plan.facility_dollars, true)
        || invalidPercent(plan));
      if (invalid) {
        this.tierError = 'Every price point needs a name, a max wrestler count of at least 1, a non-negative price, and a whole-number facility percent between 1 and 99 when using a % fee.';
        return false;
      }
      return true;
    },
    billingPlansParams() {
      return this.tierDraft.plans.map((plan) => {
        if (plan.id && plan._destroy) {
          // Existing price points may have purchase history, so archive
          // instead of destroying — matches the .active scopes used when
          // displaying plans everywhere else.
          return { id: plan.id, active: false };
        }

        // A percent selection with no value entered means "no facility fee";
        // the model requires a 1-99 percent when the type is percent, so
        // normalize that case to hourly/0 rather than failing validation.
        const percentBlank = plan.facility_percent === null || plan.facility_percent === '' || plan.facility_percent === undefined;
        const percentBased = plan.facility_fee_type === 'percent' && !percentBlank;
        const params = {
          name: plan.name,
          amount: _.round(parseFloat(plan.price_dollars) * 100),
          max_wrestler_count: plan.max_wrestler_count,
          facility_fee_type: percentBased ? 'percent' : 'hourly',
          facility_price_per_hour: (percentBased || plan.facility_dollars === '') ? 0 : _.round(parseFloat(plan.facility_dollars) * 100),
          // Whole percent in the UI, 0-1 decimal in the DB. Cleared when hourly
          // so a stale percent can never disagree with what the drawer shows.
          facility_percent_fee: percentBased ? _.round(plan.facility_percent) / 100 : null,
        };
        if (plan.id) {
          params.id = plan.id;
        } else {
          params.currency = this.currency || 'usd';
          params.usage_type = 'licensed';
          params.interval = 'one_time';
          params.inventory_type = 'infinite';
          params.active = true;
          params.sold_out = false;
        }
        return params;
      });
    },
    saveTier: _.throttle(function () {
      if (this.savingTier || !this.validateTierDraft()) {
        return;
      }

      this.savingTier = true;
      this.tierError = '';
      const isNew = !this.tierDraft.id;
      const params = {
        product: {
          name: this.tierDraft.name.trim(),
          billing_plans_attributes: this.billingPlansParams(),
        },
      };
      let request;
      if (isNew) {
        params.product.product_type = 'service';
        params.product.category = 'private_lesson';
        request = axios.post(this.$apiService.productsUrl(), params);
      } else {
        request = axios.put(this.$apiService.productUrl(this.tierDraft.id), params);
      }

      request
        .then((response) => {
          this.savingTier = false;
          if (isNew && response.data && response.data.id) {
            // Auto-assign the freshly created tier to this coach; the footer
            // Save persists the listing itself.
            this.listings.push({
              id: null,
              product_id: response.data.id,
            });
          }
          this.closeTierEditor();
          this.fetchProducts();
          this.$notificationManager.emit('show-toast', isNew ? 'Pricing tier created' : 'Prices updated', true);
        })
        .catch((error) => {
          this.savingTier = false;
          this.tierError = this.parseErrorResponse(error);
        });
    }, 500),
    // ---- Coach save ----
    saveCoach: _.throttle(function () {
      if (this.loading) {
        return;
      }
      // Saving mid-upload (or with the photo editor still open) would attach
      // a photo whose S3 object never landed. The native save button is
      // hidden while photo work runs, but this also catches the web button
      // and any race with the hide message.
      if (this.photoWorkInProgress) {
        this.error = true;
        this.errorMessage = 'Your photo is still uploading — one moment.';
        this.notifyAppOfValidationError('Photo still uploading');
        return;
      }

      this.loading = true;
      this.error = false;
      const params = {
        coach_profile: {
          accepting_private_lessons: this.accepting,
          profile_photos_attributes: this.attachmentParams,
        },
      };
      // Nothing in the drawer can change listings without the pricing section,
      // so don't send them at all rather than echoing them back.
      if (this.canEditPricing) {
        params.coach_profile.product_coach_listings_attributes = this.listingParams;
      }
      // The hidden input stays empty until trixInit loads the existing bio, so
      // only send the bio when the editor is actually live — otherwise a save
      // would blank out a bio the coach never saw.
      if (this.bioTrixEditor) {
        params.coach_profile.bio = document.getElementById('drawer-coach-bio').value;
        params.coach_profile.plain_bio = this.bioTrixEditor.getDocument().toString();
      }

      axios.put(this.$apiService.coachUrl(this.coach.id), params)
        .then(() => {
          this.loading = false;
          this.$notificationManager.emit('show-toast', `${this.coach.full_name} updated`, true);
          if (this.forAppEmbed) {
            this.askAppToPop({
              name: 'coach_profile_saved',
              data: {
                coach_id: this.coach.id,
              },
            });
            return;
          }
          this.$emit('saved');
        })
        .catch((error) => {
          this.loading = false;
          this.errorMessage = this.parseErrorResponse(error);
          this.error = true;
          // The web error line lives in the hidden footer; surface it natively
          this.notifyAppOfValidationError(this.errorMessage);
        });
    }, 500),
  },
};
</script>
